import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindMappingByTypeQueryResponse,
  FindMappingByTypePathParams,
} from "../types/FindMappingByType"

type FindMappingByTypeClient = typeof client<
  FindMappingByTypeQueryResponse,
  Error,
  never
>
type FindMappingByType = {
  data: FindMappingByTypeQueryResponse
  error: Error
  request: never
  pathParams: FindMappingByTypePathParams
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<FindMappingByTypeClient>>
  client: {
    parameters: Partial<Parameters<FindMappingByTypeClient>[0]>
    return: Awaited<ReturnType<FindMappingByTypeClient>>
  }
}
export const findMappingByTypeQueryKey = (
  type: FindMappingByTypePathParams["type"]
) =>
  [
    {
      url: "/v1/client-settings/find-mapping-by-type/:type",
      params: { type },
    },
  ] as const
export type FindMappingByTypeQueryKey = ReturnType<
  typeof findMappingByTypeQueryKey
>
export function findMappingByTypeQueryOptions<
  TData = FindMappingByType["response"],
  TQueryData = FindMappingByType["response"],
>(
  type: FindMappingByTypePathParams["type"],
  options: FindMappingByType["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindMappingByType["response"],
    FindMappingByType["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findMappingByTypeQueryKey(type)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindMappingByType["data"],
        FindMappingByType["error"]
      >({
        method: "get",
        url: `/v1/client-settings/find-mapping-by-type/${type}`,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find mapping by type
 * @link /v1/client-settings/find-mapping-by-type/:type
 */
export function useFindMappingByType<
  TData = FindMappingByType["response"],
  TQueryData = FindMappingByType["response"],
  TQueryKey extends QueryKey = FindMappingByTypeQueryKey,
>(
  type: FindMappingByTypePathParams["type"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindMappingByType["response"],
        FindMappingByType["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindMappingByType["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindMappingByType["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? findMappingByTypeQueryKey(type)
  const query = useQuery<
    FindMappingByType["data"],
    FindMappingByType["error"],
    TData,
    any
  >({
    ...findMappingByTypeQueryOptions<TData, TQueryData>(type, clientOptions),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindMappingByType["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
